/**
 * Confirmations queue (DNKADM-0256 links + account-state chip).
 *
 * Each row now deeplinks: the contact name → `/members/:contactId` (only when
 * `contactId` is present; plain text otherwise) and a "View on calendar" link →
 * `/reservations?reservation=<uuid>` (only when `reservationUuid` is present;
 * the ActivityLog/UpcomingCharges pattern). Beside the name, an account-state
 * chip (shared `accountStateBadge` derivation, DNKADM-0255) marks the booker as
 * Active/Inactive member, Login only, or Unclaimed — rendered ONLY when the
 * reservation read carries the account signal (`membershipState` + the DNKAPI-0432
 * `hasLogin` read, `Reservation.contactHasLogin`); absent signal → no chip, never a
 * guess. A contact email line renders under the name as a `mailto:` link (DNKADM-0257).
 *
 * **Unclaimed-row invite (DNKADM-0280, mirroring the DNKADM-0268 roster).** A row
 * gated on `contactHasLogin === false` (the DNKADM-0256 account-state signal) AND a
 * non-null `contactEmail` carries an Invite action beside the account chip — or,
 * when a PENDING invitation already exists for the email, "Invited {date}" + Resend.
 * It reuses the DNKADM-0168 wrappers (`inviteUser`/`resendUserInvitation`/
 * `listUserInvitations`) — the FE NEVER calls saas-auth `/internal`. The
 * pending-invitations join is loaded lazily (only when an invitable row exists),
 * keyed by lowercase email so multiple rows for one booker flip together; a
 * `409 INVITATION_ALREADY_PENDING` converges the row to Invited/Resend; a post-send
 * cooldown + single in-flight key make rapid clicks send once. Claimed/unknown
 * (`contactHasLogin` true/null) and email-less rows get NO action — absent, never
 * disabled-with-explanation. The state is mirrored from `MembersList.tsx` rather
 * than extracted: sharing it would rework the shipped roster surface (its overlay
 * is keyed by `userId` and its refresh key co-drives the Invited Users tab).
 *
 * Mutation reflection (DNKADM-0257) — optimistic update + reconciliation: a resolved
 * confirm/decline success adds the row's uuid to a local `removed` overlay so it
 * disappears immediately (it left the `unconfirmed` set), and a resolved edit/reassign
 * success layers the mutation's RETURNED reservation over the row via a `replaced`
 * overlay. The existing `refetch()` stays as reconciliation — overlays are applied on
 * top of whatever the fetch returns, never an effect-driven copy of it (mirrors
 * `InvitedUsersPanel`). Removal/replacement happens only AFTER the awaited mutation
 * resolves — never optimistically before.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  confirmReservation,
  listUnconfirmedReservations,
  overrideAcknowledgements,
  parseOverrideRefusal,
  type ConflictRefusal,
  type ConfirmReservationInput,
  type Reservation,
} from '@/api/reservations';
import {
  inviteUser,
  listUserInvitations,
  reconcileInvitation,
  resendUserInvitation,
  type UserInvitation,
} from '@/api/users';
import { IconAction } from '@/components/IconAction';
import { v2ErrorCode } from '@/api/v2Errors';
import { useToast } from '@/components/useToast';
import { useResource } from '@/hooks/useResource';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ListToolbar } from '@/components/list/ListToolbar';
import { parsePage, toPageParam, useListUrlState } from '@/components/list/useListUrlState';
import { useServiceLocation } from '@/location/useServiceLocation';
import { chipSx, Icon, inkAlpha, resolveTokens } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';
import { MemberNameLink } from '@/components/MemberNameLink';
import { contactAccountStateBadge } from '@/features/members/accountState';
import { toUserInviteErrorView } from '@/features/members/membersError';
import { CancelReservationDialog } from '@/features/reservations/ReservationActionDialogs';
import { OverrideConflictDialog } from '@/features/reservations/OverrideConflictDialog';
import {
  INVALID_WINDOW_MESSAGE,
  isInvalidWindowError,
  toReservationActionErrorMessage,
} from '@/features/reservations/reservationsError';
import { roleLimitedMessage } from '@/components/accessCopy';
import { isoClockAsIs } from '@/utils/clockTime';
import { ChangeAssignmentDialog } from './ChangeAssignmentDialog';
import { EditReservationDetailsDialog } from './EditReservationDetailsDialog';
import { notifyConfirmationQueueChanged } from './confirmationQueueEvents';
import { ScreenError } from '@/components/ScreenError';
import { useCalendarZone, useTenantZone } from '@/location/useCalendarZone';
import { resolveCalendarZone } from '@/components/list/listDate';
import { DATE_ONLY, formatInstantMs, instantMs } from '@/utils/instantFormat';
import { formatRelativeTime } from '@/utils/relativeTime';

const PAGE_SIZE = 20;

/**
 * DNKADM-0430: the 1-indexed `page` lives in the URL. DNKADM-0473 adds `q`, the server-side
 * search (booking fields plus the member's name or email, DNKAPI-0687). No sort.
 */
const URL_DEFAULTS = { page: '1', q: '' };

/** The shared search-as-you-type pause (DNKADM-0340): one request per pause. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Post-send client cooldown on a row's Invite/Resend control (DNKADM-0280 —
 * mirrors the DNKADM-0268 roster value) — with the single-in-flight key it makes
 * rapid clicks send once.
 */
const INVITE_COOLDOWN_MS = 15_000;

/** The invitation date on the venue's clock (DNKADM-0476 F-05); `—` when absent/unparseable. */
function formatInviteDate(iso: string | null, timeZone: string | null): string {
  const ms = instantMs(iso);
  return ms === null ? '—' : formatInstantMs(ms, timeZone, DATE_ONLY);
}

type DialogAction = { kind: 'edit' | 'reject' | 'assignment'; reservation: Reservation } | null;

/**
 * The date an ISO-with-offset instant carries AS WRITTEN (`Jul 21, 2026`), read off the
 * string without shifting it into the browser timezone. Null when it cannot be split.
 */
function dateAsIs(value: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T/.exec(value);
  if (!match) return null;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[Number(match[2]) - 1] ?? match[2];
  return `${month} ${Number(match[3])}, ${match[1]}`;
}

/**
 * The request's when-line (DNKADM-0386 U2): `Jul 21, 2026, 1:00 PM – 2:00 PM` — the
 * date and both clocks read off the wire strings as written (offset verbatim, the same
 * no-shift contract as before), the clocks 12-hour through the shared formatter. The
 * end's date is repeated only when it differs from the start's. Degrades to the raw
 * value when a string cannot be split, and to `—` when both are absent.
 */
function whenLabel(start: string | null, end: string | null): string {
  const startDate = dateAsIs(start);
  const endDate = dateAsIs(end);
  const startClock = isoClockAsIs(start);
  const endClock = isoClockAsIs(end);
  const startLabel = startDate && startClock ? `${startDate}, ${startClock}` : (start ?? null);
  const endLabel =
    endDate && endClock
      ? endDate === startDate
        ? endClock
        : `${endDate}, ${endClock}`
      : (end ?? null);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel ?? endLabel ?? '—';
}

/**
 * How long the request has waited (DNKADM-0476 F-04, V-49), from the booking's `createdAt`
 * (DNKAPI-0683): `Requested 2 hr. ago`. Null when the read carries no creation time (an older
 * server), so the row omits the line rather than guessing. A creation time ahead of this
 * device's clock (server clock skew) reads "just now", never "in 3 min".
 */
function waitLabel(createdAt: string | null | undefined, now: number = Date.now()): string | null {
  if (createdAt === null || createdAt === undefined) return null;
  const ms = Date.parse(createdAt);
  if (!Number.isFinite(ms)) return null;
  const relative = formatRelativeTime(createdAt, Math.max(now, ms));
  return relative === null ? null : `Requested ${relative}`;
}

function rowKey(reservation: Reservation, index: number): string {
  return reservation.reservationUuid ?? `request-${index}`;
}

/** Tenant-wide operator queue for reservations whose wire status is `unconfirmed`. */
export function ConfirmationQueueScreen() {
  const calendarZone = useCalendarZone();
  const theme = useTheme();
  const t = resolveTokens(theme);
  // DNKADM-0430: the page lives in the URL (1-indexed, `?page=2`, replaced, not pushed);
  // `page` here stays 0-indexed, as is the wire.
  const { values: urlValues, set: setUrl } = useListUrlState(URL_DEFAULTS);
  const page = parsePage(urlValues.page) - 1;
  const setPage = (next: number) => setUrl({ page: toPageParam(next + 1) });
  // A new search term restarts at the first page; the read keys off the debounced term.
  const search = urlValues.q;
  const q = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ uuid: string; message: string } | null>(null);
  const [dialog, setDialog] = useState<DialogAction>(null);
  // Confirm re-checks the booking at confirm time: the coach (DNKAPI-0542 `STAFF_CONFLICT`)
  // and, since DNKAPI-0636 D-06, every create-time gate (DNKAPI-0549 `overrideBlocks`: bay
  // full, coach off shift, coach at a cap). A refusal opens the shared dialog for THAT row
  // (DNKADM-0396) instead of the inline error — "Change it" opens Change Resource/Staff for it,
  // Cancel changes nothing, and "Book anyway" (only after the tick) re-confirms with exactly
  // the flags THAT refusal named. `sent` is the body that was refused: the guards run in
  // order, so acknowledging a coach conflict can surface a schedule block next, and the
  // second resend keeps the first acknowledgement (both were shown and ticked) rather than
  // looping on the first refusal. Nothing is remembered past this one confirm.
  const [confirmConflict, setConfirmConflict] = useState<{
    reservation: Reservation;
    view: ConflictRefusal;
    sent: ConfirmReservationInput;
  } | null>(null);
  const { locations } = useServiceLocation();
  // A reservation's dialog uses its own location's zone, else the tenant's (DNKADM-0478).
  const tenantZone = useTenantZone();
  const showToast = useToast();
  // DNKADM-0375: every action on this queue — confirm, edit time, change resource/staff,
  // decline, invite/resend — is open to a STAFF session (DNKAPI-0532), so the queue is the
  // same for every operator.

  const queue = useResource(
    () => listUnconfirmedReservations({ page, size: PAGE_SIZE, q }),
    [page, q],
  );

  // Row-local optimistic overlays (DNKADM-0257, the InvitedUsersPanel pattern):
  // actioned uuids drop out (`removed`) and edited rows render the mutation's returned
  // reservation (`replaced`), layered over every fetch result until reconciled.
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [replaced, setReplaced] = useState<Map<string, Reservation>>(new Map());
  // DNKADM-0457: a fetch result is the reconciliation, so each new one prunes the overlays
  // (adjusting state while rendering, not in an effect). A `removed` uuid the server no
  // longer lists is settled and dropped; one it still lists stays hidden (a lagging read).
  // A `replaced` row is dropped once the server no longer lists it or lists the same row;
  // a still-different (stale) row keeps the overlay on top. Without this the sets only grow.
  const [reconciledData, setReconciledData] = useState(queue.data);
  if (queue.data !== reconciledData) {
    setReconciledData(queue.data);
    if (queue.data !== null) {
      const listed = new Map(queue.data.content.map((r) => [r.reservationUuid, r]));
      if (removed.size > 0) {
        const kept = new Set([...removed].filter((uuid) => listed.has(uuid)));
        if (kept.size !== removed.size) setRemoved(kept);
      }
      if (replaced.size > 0) {
        const kept = new Map(
          [...replaced].filter(([uuid, row]) => {
            const fetched = listed.get(uuid);
            return fetched !== undefined && JSON.stringify(fetched) !== JSON.stringify(row);
          }),
        );
        if (kept.size !== replaced.size) setReplaced(kept);
      }
    }
  }

  // --- Unclaimed-row invite (DNKADM-0280, mirroring the DNKADM-0268 roster) ------
  // Loaded LAZILY: only when the queue actually contains an unclaimed row with an
  // email does the pending-invitations join fire. A load failure degrades to
  // "Invite" everywhere — the 409 ALREADY_PENDING flip below still converges a
  // stale row to the Invited/Resend state. Everything is keyed by LOWERCASE email
  // (the invite identity), so multiple queue rows for one booker flip together.
  const [invitesRefreshKey, setInvitesRefreshKey] = useState(0);
  const hasInvitableRows = (queue.data?.content ?? []).some(
    (reservation) =>
      reservation.contactHasLogin === false && reservation.contactEmail !== null,
  );
  const pendingInvitations = useResource(
    () => (hasInvitableRows ? listUserInvitations('PENDING') : Promise.resolve([])),
    [hasInvitableRows, invitesRefreshKey],
  );
  const pendingByEmail = useMemo(() => {
    const map = new Map<string, UserInvitation>();
    for (const invitation of pendingInvitations.data ?? []) {
      if (invitation.email !== null && invitation.invitationId !== '') {
        map.set(invitation.email.toLowerCase(), invitation);
      }
    }
    return map;
  }, [pendingInvitations.data]);

  // Overlay of invitations sent THIS session, the single in-flight key, and the
  // post-send cooldown set (all keyed by lowercase email).
  const [rowInvites, setRowInvites] = useState<Map<string, UserInvitation>>(new Map());
  const [inviteBusyKey, setInviteBusyKey] = useState<string | null>(null);
  const [inviteCooldowns, setInviteCooldowns] = useState<Set<string>>(new Set());
  const cooldownTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(
    () => () => {
      cooldownTimers.current.forEach(clearTimeout);
    },
    [],
  );

  function startInviteCooldown(key: string) {
    setInviteCooldowns((prev) => new Set(prev).add(key));
    cooldownTimers.current.push(
      setTimeout(() => {
        setInviteCooldowns((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }, INVITE_COOLDOWN_MS),
    );
  }

  // Reuses the DNKADM-0168 wrapper untouched — never saas-auth /internal.
  async function sendInvite(reservation: Reservation) {
    const email = reservation.contactEmail;
    if (email === null) return;
    const key = email.toLowerCase();
    setInviteBusyKey(key);
    try {
      const invitation = await inviteUser({
        email,
        ...(reservation.contactId !== null ? { contactId: reservation.contactId } : {}),
      });
      setRowInvites((prev) => new Map(prev).set(key, invitation));
      startInviteCooldown(key);
      setInvitesRefreshKey((k) => k + 1);
      showToast('Invitation sent.', 'success');
    } catch (error) {
      const view = toUserInviteErrorView(error);
      if (view.code === 'INVITATION_ALREADY_PENDING') {
        // Raced a pre-existing pending invite — treat as already-invited: the
        // refetch flips the row to Invited/Resend; no error is surfaced.
        setInvitesRefreshKey((k) => k + 1);
        showToast('An invitation is already pending for this email.');
      } else {
        showToast(view.message, 'error');
        if (view.refreshList) setInvitesRefreshKey((k) => k + 1);
      }
    } finally {
      setInviteBusyKey(null);
    }
  }

  async function resendInvite(reservation: Reservation, invitationId: string) {
    const email = reservation.contactEmail;
    if (email === null) return;
    const key = email.toLowerCase();
    setInviteBusyKey(key);
    try {
      const invitation = await resendUserInvitation(invitationId);
      setRowInvites((prev) => new Map(prev).set(key, invitation));
      startInviteCooldown(key);
      setInvitesRefreshKey((k) => k + 1);
      showToast('Invitation resent.', 'success');
    } catch (error) {
      const view = toUserInviteErrorView(error);
      showToast(view.message, 'error');
      if (view.refreshList) setInvitesRefreshKey((k) => k + 1);
    } finally {
      setInviteBusyKey(null);
    }
  }

  // The endpoint is already status-scoped, but fail closed if a stale/malformed row
  // slips through: queue membership and every action remain status-driven, never
  // phase-driven — the replaced overlay is applied BEFORE the status filter so a
  // returned row that somehow left `unconfirmed` also drops out.
  const reservations = (queue.data?.content ?? [])
    .map((reservation) =>
      reservation.reservationUuid !== null
        ? (replaced.get(reservation.reservationUuid) ?? reservation)
        : reservation,
    )
    .filter(
      (reservation) =>
        reservation.status === 'unconfirmed' &&
        (reservation.reservationUuid === null || !removed.has(reservation.reservationUuid)),
    );

  /** Optimistically drop an actioned row — called only after a RESOLVED success. */
  function markRemoved(uuid: string | null) {
    if (uuid !== null) setRemoved((prev) => new Set(prev).add(uuid));
  }

  /** Layer a mutation's returned reservation over its row (resolved success only). */
  function markReplaced(updated: Reservation) {
    const uuid = updated.reservationUuid;
    if (uuid !== null) setReplaced((prev) => new Map(prev).set(uuid, updated));
  }

  function locationName(locationId: string | null): string {
    if (!locationId) return 'Location unavailable';
    const location = locations.find((candidate) => candidate.id === locationId);
    // DNKADM-0476 F-07: an id the roster does not hold is never shown raw.
    return scrubBrand(location?.name ?? null) ?? 'Location unavailable';
  }

  function refreshAfterAction(message: string) {
    setDialog(null);
    setActionError(null);
    if (page === 0) queue.refetch();
    else setPage(0);
    notifyConfirmationQueueChanged();
    showToast(message, 'success');
  }

  /** Reload a stale queue (DNKAPI-0636 `RESERVATION_CHANGED`): page 0 refetches, others go back to it. */
  function reloadQueue() {
    if (page === 0) queue.refetch();
    else setPage(0);
    notifyConfirmationQueueChanged();
  }

  async function confirm(reservation: Reservation, input: ConfirmReservationInput = {}) {
    const uuid = reservation.reservationUuid;
    if (!uuid || reservation.status !== 'unconfirmed') return;
    setBusyUuid(uuid);
    setActionError(null);
    try {
      await confirmReservation(uuid, input);
      markRemoved(uuid); // resolved success — the row left the unconfirmed set
      refreshAfterAction('Reservation confirmed.');
    } catch (error) {
      const refusal = parseOverrideRefusal(error);
      const code = v2ErrorCode(error);
      if (refusal) {
        setConfirmConflict({ reservation, view: refusal, sent: input });
      } else if (code === 'RESERVATION_NOT_UNCONFIRMED' || code === 'RESERVATION_CHANGED') {
        // DNKAPI-0636 D-04: RESERVATION_CHANGED = another write changed the request first and
        // nothing was written; the row on screen is stale, so reload rather than retry blind.
        if (page === 0) queue.refetch();
        else setPage(0);
        notifyConfirmationQueueChanged();
        showToast(
          code === 'RESERVATION_CHANGED'
            ? 'This request was changed by someone else. The queue has been refreshed; check it and confirm again.'
            : 'This request was already actioned. The queue has been refreshed.',
        );
      } else {
        setActionError({ uuid, message: toReservationActionErrorMessage(error) });
      }
    } finally {
      setBusyUuid(null);
    }
  }

  const totalPages = queue.data?.totalPages ?? 0;
  // A linked page past the end (a stale or shared link, or a reload after the queue
  // shrank) is never rewritten. It keeps its pager, so the operator always has a way back,
  // and Previous from there goes to the last page that exists.
  const pastEnd = page > 0 && page >= totalPages;

  return (
    <Box>
      <Typography variant="h1" component="h1">Confirmations</Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Review booking requests that need operator approval before they are confirmed.
      </Typography>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next, page: null }),
          placeholder: 'Search by member name, email or booking',
          label: 'Search pending requests',
        }}
        onClear={() => setUrl({ q: null, page: null })}
      />

      {queue.status === 'loading' && queue.data === null && (
        <Stack direction="row" role="status" sx={{ alignItems: 'center', gap: 1.5, py: 6 }}>
          <CircularProgress size={22} />
          <Typography>Loading pending requests…</Typography>
        </Stack>
      )}

      {queue.status === 'error' && (
        <ScreenError
          error={queue.error}
          what="the reservations"
          onRetry={queue.refetch}
          forbiddenMessage={roleLimitedMessage('This screen', 'OWNER or ADMIN')}
          retryMessage="The reservations could not be loaded. Please try again."
          preferServerMessage
          rejectedMessage={isInvalidWindowError(queue.error) ? INVALID_WINDOW_MESSAGE : undefined}
          sx={{ mt: 2 }}
        />
      )}

      {queue.status !== 'error' && queue.data !== null && (
        <Paper elevation={1} sx={{ mt: 2.25, px: 3, py: 1 }}>
          {reservations.length === 0 && page > 0 ? (
            // Never the all-clear past the first page: earlier pages may still hold requests.
            <Box sx={{ py: 7, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>No requests on this page</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Go back to an earlier page to see the pending requests.
              </Typography>
            </Box>
          ) : reservations.length === 0 && q.length > 0 ? (
            <Box sx={{ py: 7, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>No requests match this search</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Try another name, email or booking detail.
              </Typography>
            </Box>
          ) : reservations.length === 0 ? (
            <Box sx={{ py: 7, textAlign: 'center' }}>
              <Icon name="task_alt" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
              <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
                No pending requests
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                New requests that require approval will appear here.
              </Typography>
            </Box>
          ) : (
            reservations.map((reservation, index) => {
              const uuid = reservation.reservationUuid;
              const waited = waitLabel(reservation.createdAt);
              const contactLabel = scrubBrand(
                reservation.contactName ?? reservation.contactEmail ?? 'Unnamed contact',
              );
              const accountBadge = contactAccountStateBadge(
                {
                  // The DNKAPI-0432 `hasLogin` read is the queue's claimed signal:
                  // explicit false = no login = the Unclaimed guest/booker state.
                  claimed: reservation.contactHasLogin,
                  membershipState: reservation.contactMembershipState,
                },
                theme.palette.mode,
              );
              // DNKADM-0280 invite gate: unclaimed (explicit hasLogin false) AND an
              // email to send to. Claimed/unknown/email-less rows get NO action.
              const inviteKey =
                reservation.contactHasLogin === false &&
                reservation.contactEmail !== null
                  ? reservation.contactEmail.toLowerCase()
                  : null;
              // Session overlay leads but is parsed from the TRIMMED write
              // response (no createdAt) — reconcile with the fetched row so
              // "Invited {date}" renders immediately and a resend keeps the
              // original invite date (DNKADM-0304 U1; mirrors MembersList,
              // the DNKADM-0280 mirror-not-extract decision).
              const inviteOverlay = inviteKey !== null ? (rowInvites.get(inviteKey) ?? null) : null;
              const fetchedInvitation =
                inviteKey !== null ? (pendingByEmail.get(inviteKey) ?? null) : null;
              const pendingInvitation =
                inviteOverlay !== null
                  ? reconcileInvitation(inviteOverlay, fetchedInvitation)
                  : fetchedInvitation;
              const inviteBusy = inviteKey !== null && inviteBusyKey === inviteKey;
              const inviteCooling = inviteKey !== null && inviteCooldowns.has(inviteKey);
              return (
                <Box
                  key={rowKey(reservation, index)}
                  sx={{ py: 2, borderBottom: index === reservations.length - 1 ? 'none' : `1px solid ${t.lineSoft}` }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 2, alignItems: { md: 'center' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          <MemberNameLink name={contactLabel} contactId={reservation.contactId} />
                        </Typography>
                        {accountBadge !== null &&
                          (accountBadge.hint ? (
                            <Tooltip title={accountBadge.hint}>
                              <Chip size="small" label={accountBadge.label} sx={chipSx(accountBadge)} />
                            </Tooltip>
                          ) : (
                            <Chip size="small" label={accountBadge.label} sx={chipSx(accountBadge)} />
                          ))}
                        {/* Status wins over the misleading derived phase for every queue row. */}
                        <Chip
                          size="small"
                          label="Pending confirmation"
                          sx={{ bgcolor: t.warnBadgeBg, color: t.warnBadgeInk, fontWeight: 600 }}
                        />
                        {/* Invite/Resend for an unclaimed booker (DNKADM-0280) — lives
                            with the contact identity block, not the action cluster. */}
                        {inviteKey !== null &&
                          (pendingInvitation !== null ? (
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                Invited {formatInviteDate(pendingInvitation.createdAt, calendarZone)}
                              </Typography>
                              <Button
                                size="small"
                                disabled={inviteBusy || inviteCooling}
                                aria-label={`Resend invitation to ${contactLabel}`}
                                data-ask-dink-private
                                onClick={() =>
                                  void resendInvite(reservation, pendingInvitation.invitationId)
                                }
                              >
                                {inviteBusy ? 'Resending…' : 'Resend'}
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={inviteBusy || inviteCooling}
                              aria-label={`Invite ${contactLabel}`}
                              data-ask-dink-private
                              onClick={() => void sendInvite(reservation)}
                            >
                              {inviteBusy ? 'Inviting…' : 'Invite'}
                            </Button>
                          ))}
                      </Stack>
                      {/* Contact email under the name (DNKADM-0257) — an actionable
                          mailto:, omitted when the read carries no email. */}
                      {reservation.contactEmail && (
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          <Link
                            href={`mailto:${reservation.contactEmail}`}
                            sx={{ wordBreak: 'break-all' }}
                          >
                            {reservation.contactEmail}
                          </Link>
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {scrubBrand(reservation.serviceName ?? 'Service unavailable')}
                        {reservation.variationName ? ` · ${scrubBrand(reservation.variationName)}` : ''}
                      </Typography>
                      <Stack direction="row" sx={{ mt: 0.25, color: 'text.secondary', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="inherit">
                          {whenLabel(reservation.start, reservation.end)} ·
                        </Typography>
                        <Typography variant="body2" color="inherit">{locationName(reservation.locationId)}</Typography>
                        {uuid !== null && (
                          <>
                            <Typography variant="body2" color="inherit">·</Typography>
                            <Link
                              component={RouterLink}
                              variant="body2"
                              to={`/reservations?reservation=${encodeURIComponent(uuid)}`}
                            >
                              View on calendar
                            </Link>
                          </>
                        )}
                      </Stack>
                      {waited !== null && (
                        <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>
                          {waited}
                        </Typography>
                      )}
                      {(reservation.assignedStaffName || reservation.assignedResourceName) && (
                        <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>
                          {[reservation.assignedStaffName, reservation.assignedResourceName]
                            .filter((value): value is string => value !== null)
                            .map(scrubBrand)
                            .join(' · ')}
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => void confirm(reservation)}
                        disabled={!uuid || busyUuid === uuid}
                      >
                        {busyUuid === uuid ? 'Confirming…' : 'Confirm'}
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => setDialog({ kind: 'edit', reservation })}>
                        Edit time
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDialog({ kind: 'assignment', reservation })}
                      >
                        Change Resource/Staff
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => setDialog({ kind: 'reject', reservation })}
                      >
                        Decline request
                      </Button>
                    </Stack>
                  </Stack>
                  {uuid && actionError?.uuid === uuid && (
                    <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>{actionError.message}</Alert>
                  )}
                </Box>
              );
            })
          )}

          {(totalPages > 1 || page > 0) && (
            <Stack component="nav" aria-label="Pagination" direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: 1, py: 1.5, borderTop: `1px solid ${t.lineFaint}` }}>
              <Typography variant="body2" aria-current="page">
                {pastEnd ? `Page ${page + 1}` : `Page ${page + 1} of ${totalPages}`}
              </Typography>
              <IconAction label="Previous page" size="small" onClick={() => setPage(pastEnd ? Math.max(0, totalPages - 1) : Math.max(0, page - 1))} disabled={page === 0}>
                <Icon name="chevron_left" />
              </IconAction>
              <IconAction label="Next page" size="small" onClick={() => setPage(page + 1)} disabled={page + 1 >= totalPages}>
                <Icon name="chevron_right" />
              </IconAction>
            </Stack>
          )}
        </Paper>
      )}

      {/* Edit/assignment keep the row in the queue — a resolved success layers the
          RETURNED reservation over it (DNKADM-0257); decline removes it. All three
          keep refreshAfterAction's refetch as reconciliation. */}
      {dialog?.kind === 'edit' && (
        <EditReservationDetailsDialog
          key={`edit-${dialog.reservation.reservationUuid ?? 'unknown'}`}
          open
          reservation={dialog.reservation}
          onClose={() => setDialog(null)}
          onDone={(message, updated) => {
            if (updated) markReplaced(updated);
            refreshAfterAction(message);
          }}
          onStale={reloadQueue}
          timeZone={resolveCalendarZone(
            locations.find((l) => l.id === dialog.reservation.locationId)?.timezone,
            tenantZone,
          )}
        />
      )}
      {dialog?.kind === 'assignment' && (
        <ChangeAssignmentDialog
          key={`assignment-${dialog.reservation.reservationUuid ?? 'unknown'}`}
          open
          reservation={dialog.reservation}
          onClose={() => setDialog(null)}
          onDone={(message, updated) => {
            if (updated) markReplaced(updated);
            refreshAfterAction(message);
          }}
          onStale={reloadQueue}
        />
      )}
      {dialog?.kind === 'reject' && (
        <CancelReservationDialog
          key={`reject-${dialog.reservation.reservationUuid ?? 'unknown'}`}
          open
          reservation={dialog.reservation}
          onClose={() => setDialog(null)}
          onDone={(message) => {
            markRemoved(dialog.reservation.reservationUuid); // resolved cancel success
            refreshAfterAction(message);
          }}
        />
      )}
      {/* Confirm's refusal (DNKADM-0396; every gate since DNKAPI-0636) — a dialog, never the
          inline row error. Mounted only while pending so the acknowledgement tick starts clean. */}
      {confirmConflict && (
        <OverrideConflictDialog
          open
          refusal={confirmConflict.view}
          submitting={busyUuid !== null}
          onCancel={() => setConfirmConflict(null)}
          onChangeIt={() => {
            const { reservation } = confirmConflict;
            setConfirmConflict(null);
            setDialog({ kind: 'assignment', reservation });
          }}
          onBookAnyway={() => {
            const { reservation, view, sent } = confirmConflict;
            setConfirmConflict(null);
            // Exactly the flags THIS refusal named, on top of the body it refused — never
            // a flag for a decision the admin was not shown.
            void confirm(reservation, { ...sent, ...overrideAcknowledgements(view) });
          }}
        />
      )}
    </Box>
  );
}
